import { Component, OnInit } from '@angular/core';
import { Validators, FormArray, NgForm } from '@angular/forms';
import _ from 'lodash';
import moment from 'moment';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { StudentRecordEntityComponent } from '../student-record-entity/student-record-entity.component';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-student-inquiry-details',
  templateUrl: './student-inquiry-details.component.html',
  styleUrls: ['./student-inquiry-details.component.css']
})
export class StudentInquiryDetailsComponent extends BaseDialogComponent implements OnInit {

  override eckType = "student_accounts";
  override bundle = "student_account";
  override entityId = this._injectedDialogData['data']['EntityID'];

  // Controls whether retired (status "0") instructors/executives appear in the
  // Teacher / Executive / Inquiry Taker dropdowns. Retired people are hidden by
  // default; a retired person already attached to this record is always shown.
  showRetired = false;

  // The uid (as a string) currently saved on each staff control, resolved once
  // per change-detection cycle. The template compares each option's uid against
  // these so an already-attached retired person is never filtered out (which
  // would blank the field). Returns '' when nothing is attached. The saved value
  // may be a string, number, or {id}/{target_id}, so we normalize to a string.
  private attachedStaffId(controlName: string): string {
    const raw = this.studentAccountDetailsForm?.get(controlName)?.value;
    if (raw === null || raw === undefined || raw === '') { return ''; }
    const savedId = typeof raw === 'object' ? (raw.id ?? raw.target_id ?? raw.value) : raw;
    return String(savedId);
  }

  get attachedTeacherId(): string { return this.attachedStaffId('field_teacher'); }
  get attachedExecutiveId(): string { return this.attachedStaffId('field_executive'); }
  get attachedInquiryTakerId(): string { return this.attachedStaffId('field_inquiry_taker'); }

  /**
   * True when this dialog was opened without a saved student account id (for
   * example from a dashboard that loaded nothing). Every save here PATCHes
   * student_accounts/student_account/{entityId}; with no id that request goes
   * to .../undefined and any contact created first is left orphaned.
   */
  private get missingAccountId(): boolean {
    return this.entityId === undefined || this.entityId === null || this.entityId === '';
  }

  private readonly missingAccountMessage =
    'This inquiry is not attached to a saved student account, so it cannot be changed here. ' +
    'Open the student from the Students List, or use Add Student to create them.';

  override ngOnInit() {
    this.initActionType = this._injectedDialogData['data']['action'];
    this.actionType = this._injectedDialogData['data']['action'];

    let formGroup = {
      id: [this.entityData?.id, Validators.required],
      field_inquiry_taker: [this.entityData?.field_inquiry_taker, null],
      field_inquiry_method: [this.entityData?.field_inquiry_method, null],
      field_inquired: [this.entityData?.field_inquired, null],
      field_teacher: [this.entityData?.field_teacher, null],
      field_executive: [this.entityData?.field_executive, null],
      field_booked_on: [this.entityData?.field_booked_on, null],
      field_showed: [this.entityData?.field_showed, null],
      field_dances_type: [this.entityData?.field_dances_type || [], null],
      field_notes: [this.entityData?.field_notes, null],
      field_inquiry_studen: [this.entityData?.field_inquiry_studen, null],
      // field_contacts: new FormArray([]),
      field_contacts: new FormArray([]),
    }

    let formGroupEdit;
    formGroupEdit = {
          ...formGroup,
    }

    this.studentAccountDetailsForm = this._formBuilder.group(formGroupEdit);
  }

  onSubmitInquiryDetails(f: NgForm) {
    if (this.missingAccountId) {
      this.errorMessage = this.missingAccountMessage;
      return;
    }

    this.displayProgressSpinner(true);
    LoggerService.log('onSubmitInquiryDetails called...');

    LoggerService.log('studentAccountDetailsForm', this.studentAccountDetailsForm.value);

    // Fix for package creation not working...
    if (this.actionType == 'create') {
    }

    // Set default value for field_retired if nothing is set.
    if (this.studentAccountDetailsForm.value?.['field_retired'] == "") {
      this.studentAccountDetailsForm.value.field_retired = false;
    }

    // Handle date fields
    this.formatDateField('field_inquired');
    this.formatDateField('field_showed');
    this.formatDateField('field_booked_on');

    // If SPS code is sundry (558), Misc (554), or Non Unit (657) don't send lesson_type.
    if ((this.studentAccountDetailsForm.value?.field_lesson_type == '') && (this.studentAccountDetailsForm.value.field_sps_code == '558' || this.studentAccountDetailsForm.value.field_sps_code == '554' || this.studentAccountDetailsForm.value.field_sps_code == '657')) {
      delete (this.studentAccountDetailsForm.value.field_lesson_type);
    }

    // Submit the form
    this.onSubmit(this.studentAccountDetailsForm);
  }

  contactEntityIncrement() {
    // Without an account to attach to, the new contact would be saved and then
    // orphaned when the PATCH below hits student_account/undefined.
    if (this.missingAccountId) {
      this.errorMessage = this.missingAccountMessage;
      return;
    }

    // Open dialog to create new student contact
    this._dialogService.openDialog(StudentRecordEntityComponent, "defaultWithData", {
      data: {
        EntityID: '',
        eckType: 'contacts',
        bundle: 'student_record',
        action: 'create',
        fieldsData: {
          field_attending_lessons: 'partner' // Default to "With Partner"
        },
      },
    }).afterClosed().subscribe(result => {
      // If a contact was created, associate it with the student account
      if (result && result.id) {
        // Get the new contact ID (handle both possible formats)
        const newContactId = result.id[0]?.value || result.id;
        
        if (newContactId) {
          // Get current contacts
          const currentContacts = this.studentAccountDetailsForm.value?.field_contacts || [];
          
          // Add the new contact
          const updatedContacts = [...currentContacts, { target_id: newContactId }];
          
          // Update the student account with the new contact
          const body = {
            'field_contacts': updatedContacts
          };
          
          this._entityRESTService.patchEntity('student_accounts', 'student_account', this.entityId, body)
            .subscribe(() => {
              // Reload this component to get updated data
              this.ngOnInit();
              this.ngAfterContentInit();
            });
        } else {
          // If no ID but dialog closed, still refresh in case something changed
          this.ngOnInit();
          this.ngAfterContentInit();
        }
      } else {
        // If dialog was cancelled or closed without creating, still refresh
        this.ngOnInit();
        this.ngAfterContentInit();
      }
    });
  }

  contactEntityRemove(index: number) {
    if (this.missingAccountId) {
      this.errorMessage = this.missingAccountMessage;
      return;
    }

    const contacts = this.studentAccountDetailsForm.get('field_contacts') as FormArray;
    const contact = contacts.at(index);
    const contactValue = contact.value;

    // Remove from Drupal
    let studentContactID = contactValue.target_id;
    this._entityRESTService.deleteEntity('contacts', 'student_record', studentContactID)
    .subscribe(data => {
      // Remove the contact from the form.
      contacts.removeAt(index);

      // Sync contact(s) to student account.
      let body = {
        'field_contacts': this.studentAccountDetailsForm.value?.field_contacts
      }
      this._entityRESTService.patchEntity('student_accounts', 'student_account', this.entityId, body)
      .subscribe(data => {
        // Reload this component.
        this.ngOnInit();
        this.ngAfterContentInit();
      });
    });
  }

  studentUpdateLoad(index: number) {
    LoggerService.log(index, (<FormArray>this.studentAccountDetailsForm.get('field_contacts')).value?.[index]);
    let studentContactID = (<FormArray>this.studentAccountDetailsForm.get('field_contacts')).value?.[index]?.['target_id'];
    this._dialogService.openDialog(StudentRecordEntityComponent, "defaultWithData", {
      data: {
        EntityID: studentContactID,
        eckType: 'contacts',
        bundle: 'student_record',
        action: 'edit',
        fieldsData: '',
      },
    }).afterClosed().subscribe(data => {
      // Reload this component.
      this.ngOnInit();
      this.ngAfterContentInit();
    });
  }

  formatDateField(fieldName: string) {
    const fieldValue = this.studentAccountDetailsForm.get(fieldName)?.value;
    if (fieldValue && moment(fieldValue).isValid()) {
      this.studentAccountDetailsForm.get(fieldName).setValue(moment(fieldValue).format('YYYY-MM-DD'));
    } else {
      this.studentAccountDetailsForm.get(fieldName).setValue(null);
    }
  }

  getContactsFormArray() {
    return this.studentAccountDetailsForm.get('field_contacts') as FormArray;
  }

  getActiveContactsCount(): number {
    const contacts = this.studentAccountDetailsForm?.value?.field_contacts || [];
    return contacts.filter(c => c.field_archived !== '1').length;
  }


}
